/**
 * Shared Tailwind classes for forms across the app.
 * - Controls use the same height (h-10), border, shadow, and focus ring.
 * - “Stack” variants omit `mt-1` for label-less grids (`gap-3` / `gap-4`).
 */

export const labelClass = 'block text-xs font-medium text-slate-600';

export const focusRingClass =
  'focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30';

/** Single-line text/password/email (stacked in a grid, no label row above). */
export const inputStackFieldClass = [
  'h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm',
  'placeholder:text-slate-400',
  focusRingClass,
].join(' ');

/** Same as inputStackFieldClass but monospace (import keys, hex). */
export const inputMonoStackFieldClass = [
  'h-10 w-full rounded-lg border border-slate-300 bg-white px-3 font-mono text-sm text-slate-900 shadow-sm',
  'placeholder:text-slate-400',
  focusRingClass,
].join(' ');

/** Native `<select>` — chain / wallet / token pickers (with label above). */
export const selectFieldClass = [
  'mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm',
  focusRingClass,
].join(' ');

/** Native `<select>` in grids / admin rows (no label, same chrome as inputs). */
export const selectStackFieldClass = [
  'h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm',
  focusRingClass,
].join(' ');

/** Compact `<select>` (language switcher, filter dropdowns). */
export const selectCompactFieldClass = [
  'h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-800 shadow-sm',
  focusRingClass,
].join(' ');

/** Plain text inputs with label above (password, slippage number field, etc.). */
export const inputFieldClass = ['mt-1', inputStackFieldClass].join(' ');

/** Amounts, fees, addresses typed by users (label above). */
export const inputMonoFieldClass = ['mt-1', inputMonoStackFieldClass].join(' ');

/** Multi-line secrets / notes (label above). */
export const textareaFieldClass = [
  'mt-1 min-h-[4.5rem] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-xs leading-relaxed text-slate-900 shadow-sm',
  'placeholder:text-slate-400',
  focusRingClass,
].join(' ');

/** Primary CTA — fixed bar on mobile, inline width on `sm+` (transfer / swap submit). */
export const primarySubmitButtonClass =
  'h-10 w-full rounded-lg bg-blue-600 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-6';

/** Primary CTA — full width (login, settings sections). */
export const primaryButtonBlockClass =
  'h-10 w-full rounded-lg bg-blue-600 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60';

/** Prominent primary CTA (home “create wallet” hero). */
export const primaryButtonProminentClass =
  'h-12 w-full rounded-lg bg-blue-600 text-base font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60';

/** Dark secondary CTA (settings “save login password”). */
export const secondaryDarkButtonClass =
  'h-10 w-full rounded-lg bg-slate-800 text-sm font-medium text-white shadow-sm transition hover:bg-slate-900 disabled:opacity-60';

/** Dark secondary CTA — inline width (admin import in grid). */
export const secondaryDarkButtonInlineClass =
  'h-10 shrink-0 rounded-lg bg-slate-800 px-4 text-sm font-medium text-white shadow-sm transition hover:bg-slate-900 disabled:opacity-60';

/** Outline / refresh-style buttons on home header. */
export const outlineSoftButtonClass =
  'h-9 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50';

/** Full-width outline (modal dismiss, secondary cancel). */
export const outlineButtonBlockClass =
  'h-10 w-full rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-800 shadow-sm transition hover:bg-slate-50';

/** Inline outline (form row cancel next to primary). */
export const outlineButtonInlineClass =
  'h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:opacity-60';

/** Destructive inline submit (e.g. disable master key). */
export const dangerSubmitInlineClass =
  'h-10 rounded-lg bg-red-600 px-4 text-sm font-medium text-white shadow-sm transition hover:bg-red-700 disabled:opacity-60';

/** Full-width success / warning CTAs (wrap / unwrap). */
export const successButtonBlockClass =
  'h-10 w-full rounded-lg bg-emerald-600 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50';

export const warningButtonBlockClass =
  'h-10 w-full rounded-lg bg-amber-600 text-sm font-medium text-white shadow-sm transition hover:bg-amber-700 disabled:opacity-50';
